import type { CSSProperties, ReactElement, ReactNode } from "react";
import { ArrowLeft } from "lucide-react";
import { layout, space } from "@tokens/index";
import { IconButton, Surface, Text, useViewport } from "@primitives/index";
import { contentGutter } from "./shellMeasures";

/**
 * WorkspaceHome: the queues ↔ work items master–detail.
 *
 *  - `expanded`+  rail at `RAIL_WIDTH` beside the focus column. The rail
 *                 does not grow on `wide`; the grid is capped and centered
 *                 at `WORKSPACE_MAX_WIDTH`.
 *  - `medium`     rail at `layout.railWidthNarrow` beside the focus column.
 *  - `compact`    one at a time. No selection → the rail fills the width.
 *                 A selection → the focus column fills the width under a
 *                 sub-header with a back control. The URL carries the
 *                 selection; this component reads it and never invents it.
 *
 * The region owns its horizontal gutter (the shell frame hands it over).
 */

export interface WorkspaceHomeProps {
  /** The queue list. */
  readonly rail: ReactNode;
  /** The selected queue's work items, or `null` when nothing is selected. */
  readonly focus: ReactNode | null;
  readonly selectedQueueId: string | null;
  readonly selectedQueueName: string | null;
  /** Clears the selection. On `compact` this is the back action. */
  readonly onClearSelection: () => void;
}

// Local measures. `layout.railWidth` (320) and `layout.workspaceMaxWidth`
// (1440) are the tokens' values; the workspace uses a narrower rail and a
// wider cap. Fold these into `breakpoints.ts` when the tokens are revised.
const RAIL_WIDTH = 300;
const WORKSPACE_MAX_WIDTH = 1600;

const scrollRegionStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minHeight: 0,
  alignItems: "center",
  width: "100%",
  boxSizing: "border-box",
  overflowY: "auto",
  overscrollBehavior: "contain",
  paddingTop: space[3],
  paddingBottom: space[6],
};

const compactHeaderStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: space[2],
  minHeight: 40,
  marginBottom: space[2],
  minWidth: 0,
};

const emptyFocusStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: 160,
  textAlign: "center",
};

function EmptyFocus(): ReactElement {
  return (
    <Surface elevation={1} borderRadius="lg" padding={6} style={emptyFocusStyle}>
      <Text role="bodySm" tone="muted" style={{ maxWidth: layout.proseMaxWidth }}>
        Select a queue to see its work items.
      </Text>
    </Surface>
  );
}

export function WorkspaceHome({
  rail,
  focus,
  selectedQueueId,
  selectedQueueName,
  onClearSelection,
}: WorkspaceHomeProps): ReactElement {
  const { viewport, isCompact } = useViewport();
  const regionStyle: CSSProperties = {
    ...scrollRegionStyle,
    paddingInline: contentGutter(viewport),
  };

  if (isCompact) {
    const showFocus = selectedQueueId !== null;
    return (
      <div style={regionStyle} data-workspace-home="compact">
        <div style={{ width: "100%", minWidth: 0 }}>
          {showFocus ? (
            <>
              <div style={compactHeaderStyle}>
                <IconButton
                  icon={ArrowLeft}
                  aria-label="Back to queues"
                  tooltip="Back to queues"
                  onClick={onClearSelection}
                />
                <Text role="subheading" truncate>
                  {selectedQueueName ?? ""}
                </Text>
              </div>
              {focus}
            </>
          ) : (
            rail
          )}
        </div>
      </div>
    );
  }

  const railWidth = viewport === "medium" ? layout.railWidthNarrow : RAIL_WIDTH;
  const splitStyle: CSSProperties = {
    display: "grid",
    gridTemplateColumns: `${String(railWidth)}px minmax(0, 1fr)`,
    gap: viewport === "medium" ? space[5] : space[6],
    width: "100%",
    maxWidth: WORKSPACE_MAX_WIDTH,
    alignItems: "start",
  };

  return (
    <div style={regionStyle} data-workspace-home={viewport}>
      <div style={splitStyle}>
        {rail}
        {focus ?? <EmptyFocus />}
      </div>
    </div>
  );
}
